import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Box, Button, TextField, Typography, Paper, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { changePassword } from '../../store/slices/authSlice';

export default function ChangePassword() {
  const location = useLocation();
  const fromSettings = location.state?.from === 'settings';
  const { mustChangePassword } = useSelector((s) => s.auth);
  const isFirstLogin = mustChangePassword && !fromSettings;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((s) => s.auth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      enqueueSnackbar('Le mot de passe doit contenir au moins 6 caractères.', { variant: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      enqueueSnackbar('Les mots de passe ne correspondent pas.', { variant: 'error' });
      return;
    }
    const result = await dispatch(
      changePassword({
        newPassword,
        isFirstLogin,
        ...(isFirstLogin ? {} : { currentPassword }),
      })
    );
    if (changePassword.fulfilled.match(result)) {
      enqueueSnackbar(isFirstLogin ? 'Mot de passe défini avec succès.' : 'Mot de passe modifié avec succès.', {
        variant: 'success',
      });
      navigate(isFirstLogin || fromSettings ? '/dashboard/settings' : '/dashboard');
    } else {
      enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
    }
  };

  const overlayOpacity = 0.01;

  return (
    <Box
      className="min-h-screen flex items-center justify-center"
      sx={{
        position: 'relative',
        p: 2,
        backgroundImage: 'url(/ung-login.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: `rgba(255,255,255,${overlayOpacity})`,
          backdropFilter: 'blur(4px)',
          zIndex: 0,
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420 }}
      >
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
          {fromSettings && (
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard/settings')}
              sx={{ mb: 2 }}
              size="small"
            >
              Retour aux paramètres
            </Button>
          )}
          <Typography variant="h6" gutterBottom color="primary">
            {isFirstLogin ? 'Définir un nouveau mot de passe' : 'Changer le mot de passe'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isFirstLogin
              ? 'Pour votre sécurité, définissez un mot de passe que vous seul connaissez.'
              : 'Pour modifier votre mot de passe, saisissez d\'abord votre mot de passe actuel.'}
          </Typography>
          <form onSubmit={handleSubmit}>
            {!isFirstLogin && (
              <TextField
                fullWidth
                label="Mot de passe actuel"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end">
                        {showCurrent ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth
              label="Nouveau mot de passe"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              inputProps={{ minLength: 6 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(!showNew)} edge="end">
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              inputProps={{ minLength: 6 }}
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? 'Les mots de passe ne correspondent pas'
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={{ py: 1.5 }}>
              {loading ? <CircularProgress size={24} /> : isFirstLogin ? 'Enregistrer le mot de passe' : 'Modifier le mot de passe'}
            </Button>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
}
